/**
 * 听写播放器 —— **可测的播放引擎**（纯逻辑 + 显式注入的三种能力）。
 *
 * ## 为什么不是一个 `useEffect` 里堆定时器
 *
 * "读几遍、隔几秒、跳到第几题、暂停后从哪儿继续"这套时序是听写能不能用的关键，
 * 而它是**没法用肉眼看出来对错**的（要听完一整轮才发现少停了一次）。
 * 所以这里把 DOM 那三件事抽成注入的能力（`speak` / `audio` / `clock`），
 * 引擎自己只做时序与状态迁移 —— 于是 `dictationPlayer.test.ts` 可以用
 * **假时钟**在 1 毫秒内把 20 个词、7 分钟的听写整轮跑完并逐格断言。
 *
 * ## 三种"出声"能力（都由调用方注入，本文件不碰 DOM）
 *
 * | 能力 | 真实实现 | 测试替身 |
 * | --- | --- | --- |
 * | `speak` | `speechSynthesis.speak()`（只能读**文字**，读不了拼音） | 记录念了什么 |
 * | `audio` | 一个 `<audio>` 元素播家长的录音 | 记录放了哪一段 |
 * | `clock` | `setTimeout` + `Date.now()` | 立刻到期的假时钟 |
 *
 * ## ⚠️ 自动播放限制（这是硬约束，不是体验优化）
 *
 * `speechSynthesis.speak()` 与 `HTMLMediaElement.play()` 都要求**已经发生过用户手势**。
 * 页面加载时自动出声会被浏览器拦掉（iOS Safari 上表现为**完全没声音、也没有报错**）。
 * 所以引擎的状态里有 `gesture: boolean`，而 `play()` **只在 `gesture === true`
 * 时有效**：界面必须先渲染一个「开始播放」按钮，用户点了才 `start()`。
 *
 * ## 语音合成读错音怎么办
 *
 * `speak` 拿到的文本**永远是词本身**（`spokenText`）—— `speechSynthesis` 读不了拼音，
 * 而多音字它一定会读错。唯一的出路是**家长录音覆盖**：那种题目的 `spokenSource`
 * 是 `parent_audio`，引擎走 `audio` 那条路，**不会**再叠一遍 TTS。
 */
import {
  advancePlayback,
  buildPlaybackPlan,
  currentPosition,
  currentStep,
  playbackProgress,
  seekToWord,
  type DictationTempoConfig,
  type PlaybackPlan,
  type PlaybackStep,
  type PlaybackState,
  type SpokenSource,
} from '@fht/shared';

/** 引擎需要知道的、关于一道题的信息（由会话详情映射而来）。 */
export interface PlayerQuestion {
  id: string;
  /** 题号，从 1 开始。 */
  position: number;
  wordId: string | null;
  wordText: string;
  /** 朗读来源：`parent_audio` = 用家长录音（覆盖 TTS）。 */
  spokenSource: SpokenSource;
  /** 用 TTS 时念的文字（**永远是词本身**）。 */
  spokenText: string;
  /** 家长录音的真实时长（毫秒）；拿不到就是 `null`（于是按字数估算）。 */
  audioDurationMs: number | null;
  /** 有录音时的接口路径（引擎只把它交给 `audio`，不自己拼 URL）。 */
  audioPath: string | null;
}

/** 读取一处"当前时间/稍后再来"的能力。 */
export interface PlayerClock {
  now(): number;
  schedule(handler: () => void, delayMs: number): () => void;
}

/** 语音合成（只吃文字）。 */
export interface SpeechEngine {
  /** 开始念一段文字。 */
  speak(text: string, handlers: { onEnd: () => void; onError: () => void }): void;
  /** 打断当前朗读（暂停/跳过时用）。 */
  cancel(): void;
  /** 这个环境有没有可用的中文语音（`false` 时界面要提示"这台设备不能朗读"）。 */
  isAvailable(): boolean;
}

/** 家长录音的播放。 */
export interface AudioEngine {
  /** 放一段录音；`onEnded` 在自然播完时回调，`onError` 在放不出来时回调。 */
  play(path: string, handlers: { onEnded: () => void; onError: () => void }): void;
  stop(): void;
}

export interface PlayerDeps {
  clock: PlayerClock;
  speech: SpeechEngine;
  audio: AudioEngine;
  /**
   * 进度上报（"这一题读过了"）。**不是**对错 —— 批改是第四步。
   * 引擎按题去重：同一题只报一次（重播不重复发请求）。
   */
  markProgress(questionId: string, status: 'played' | 'skipped'): void;
}

export interface PlayerSnapshot {
  /** 已经播完了多少格。 */
  stepIndex: number;
  playing: boolean;
  finished: boolean;
  /** 是否已经发生过用户手势（**没有它就不能出声**）。 */
  gesture: boolean;
  /** 当前格的序号（1 开始）；`null` = 还没开始或已结束。 */
  repeat: number | null;
  /** 当前正在处理哪一题；`null` = 空会话。 */
  position: number | null;
  /** 当前是不是在等（等的时候界面显示倒计时，孩子知道还要等多久）。 */
  waiting: boolean;
  /** 等待结束后的时刻（`clock.now()` 的同一个时钟）；不在等就是 `null`。 */
  targetAt: number | null;
  /** 已走完的毫秒（估算/真实混合；用来画进度条）。 */
  elapsedMs: number;
  totalMs: number;
  progress: number;
}

/** 默认时钟：`setTimeout` + `Date.now()`（浏览器与 Node 都能用）。 */
export const realClock: PlayerClock = {
  now: () => Date.now(),
  schedule: (handler, delayMs) => {
    const timer = setTimeout(handler, Math.max(0, delayMs));
    return () => clearTimeout(timer);
  },
};

interface SequenceTiming {
  /** 这一格预计什么时候结束。 */
  target: number;
  /** 这一格是不是**刚刚开始**（保证 `begin` 只调一次，暂停后继续不重复出声）。 */
  fresh: boolean;
  /** 已经开始念/放了（等它的结束信号）。 */
  speaking: boolean;
}

export class DictationPlayer {
  private readonly deps: PlayerDeps;
  private readonly plan: PlaybackPlan;
  private readonly byPosition: Map<number, PlayerQuestion>;
  private readonly listeners = new Set<() => void>();
  private readonly marks = new Map<string, 'played' | 'skipped'>();

  private state: PlaybackState = {
    stepIndex: 0,
    playing: false,
    finished: false,
    startedSinceGesture: false,
  };
  private timing: SequenceTiming | null = null;
  private cancelTimer: (() => void) | null = null;
  private snapshot: PlayerSnapshot;

  constructor(questions: readonly PlayerQuestion[], tempo: DictationTempoConfig, deps: PlayerDeps) {
    this.deps = deps;
    this.byPosition = new Map(questions.map((q) => [q.position, q]));
    this.plan = buildPlaybackPlan(
      questions.map((q) => ({
        position: q.position,
        source: q.spokenSource,
        durationMs: q.audioDurationMs,
        speakText: q.spokenText || q.wordText,
      })),
      tempo,
    );
    this.snapshot = this.computeSnapshot();
  }

  // ---------------------------------------------------------------- 订阅

  getSnapshot = (): PlayerSnapshot => this.snapshot;

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  private emit(): void {
    this.snapshot = this.computeSnapshot();
    for (const listener of [...this.listeners]) listener();
  }

  private computeSnapshot(): PlayerSnapshot {
    const step = currentStep(this.state, this.plan);
    const position = currentPosition(this.state, this.plan);
    let elapsedMs = 0;
    for (let i = 0; i < this.state.stepIndex && i < this.plan.steps.length; i += 1) {
      elapsedMs += this.plan.steps[i]!.durationMs;
    }
    return {
      stepIndex: this.state.stepIndex,
      playing: this.state.playing,
      finished: this.state.finished,
      gesture: this.state.startedSinceGesture,
      repeat: step?.kind === 'word' ? step.repeat : null,
      position,
      waiting: step !== null && step.kind !== 'word',
      targetAt: this.timing?.target ?? null,
      elapsedMs,
      totalMs: this.plan.totalMs,
      progress: playbackProgress(this.state, this.plan),
    };
  }

  // ---------------------------------------------------------------- 对外命令

  /** 时间线（"第 3 题 / 共 20 题"的题号与总时长都能从它推）。 */
  getPlan(): PlaybackPlan {
    return this.plan;
  }

  /**
   * 用户点了「开始播放」（**这是唯一打开手势的入口**）。
   *
   * 已经播完时再点 = 从头再来一遍（家长的真实动作："刚才没听清，再放一次"）。
   */
  start(): void {
    this.cancelPending();
    this.state = {
      ...this.state,
      // 播完之后再点「开始」= 从头再来一遍（家长的真实动作："刚才没听清，再放一次"）
      stepIndex: this.state.finished ? 0 : this.state.stepIndex,
      playing: true,
      finished: false,
      // ⚠️ 这一行是**唯一**把"已经发生过用户手势"置真的地方：
      //    没有它，`speak()` / `play()` 会被浏览器拦掉（iOS 上完全没声音、也不报错）。
      startedSinceGesture: true,
    };
    this.timing = null;
    this.tick();
    this.emit();
  }

  resume(): void {
    if (!this.state.startedSinceGesture) return; // 没手势就不出声
    this.state = { ...this.state, playing: true };
    this.tick();
    this.emit();
  }

  pause(): void {
    /**
     * ⚠️ 这里**不能**把 `this.timing` 清成 `null`。
     *
     * `timing === null` 的语义是"这一格还没开始"（`tick` 看到它就调 `beginStep`）。
     * 暂停时清掉它，继续播放就会**把当前这一格重新念一遍** —— 孩子会听到
     * "同一个词连着读三遍"。正确做法是**保留这一格的进度**（`speaking` 与目标时刻），
     * 继续时接着原来的节奏走。
     *
     * 但停声与清定时器必须做：`speak()` / `play()` 已经发出去了，不取消就会
     * 在暂停期间继续出声。
     */
    this.cancelPending();
    this.deps.speech.cancel();
    this.deps.audio.stop();
    this.state = { ...this.state, playing: false };
    this.emit();
  }

  /** 用户想要"暂停/继续"时的单一入口（界面只摆一个按钮）。 */
  toggle(): void {
    if (this.state.playing) this.pause();
    else if (this.state.stepIndex === 0 && !this.state.finished) this.start();
    else this.resume();
  }

  /** 重播当前这一题（从这一题的第一遍开始）。 */
  replayCurrent(): void {
    const position = currentPosition(this.state, this.plan);
    if (position === null) return;
    this.seek(position);
  }

  /** 跳到某一题（下一题 / 手点题号）。 */
  seek(position: number): void {
    const before = currentPosition(this.state, this.plan);
    this.cancelPending();
    this.deps.speech.cancel();
    this.deps.audio.stop();
    this.timing = null;
    this.state = seekToWord(this.state, this.plan, position);
    if (this.state.playing) this.tick();
    this.emit();
    if (before !== position) this.mark(position, 'played');
  }

  /** 跳过当前这一题（记成 `skipped`，第四步能看出"这道题没听"）。 */
  skipCurrent(): void {
    const position = currentPosition(this.state, this.plan);
    if (position === null) return;
    this.mark(position, 'skipped');
    const next = this.plan.steps.find(
      (step) => step.kind === 'word' && step.wordPosition > position,
    );
    if (next === undefined) {
      this.finish();
      return;
    }
    this.seekTo(next.wordPosition);
  }

  /** 结束播放（家长点了「结束」）：停声、标记完成，但不自己写会话状态。 */
  stop(): void {
    this.cancelPending();
    this.deps.speech.cancel();
    this.deps.audio.stop();
    this.timing = null;
    this.state = { ...this.state, playing: false, finished: true, stepIndex: this.plan.steps.length };
    this.emit();
  }

  /** 会话正在播放的会话 id（供上报用）—— 引擎不关心它，由调用方兜住。 */
  getCurrentQuestion(): PlayerQuestion | null {
    const position = currentPosition(this.state, this.plan);
    return position === null ? null : (this.byPosition.get(position) ?? null);
  }

  /** 已经上报过的进度（去重后的）。 */
  getReportedProgress(): { questionId: string; status: 'played' | 'skipped' }[] {
    return [...this.marks.entries()].map(([questionId, status]) => ({ questionId, status }));
  }

  // ---------------------------------------------------------------- 内部

  private seekTo(position: number): void {
    this.cancelPending();
    this.deps.speech.cancel();
    this.deps.audio.stop();
    this.timing = null;
    this.state = seekToWord(this.state, this.plan, position);
    if (this.state.playing) this.tick();
    this.emit();
  }

  private finish(): void {
    this.cancelPending();
    this.deps.speech.cancel();
    this.deps.audio.stop();
    this.timing = null;
    this.state = { ...this.state, playing: false, finished: true };
    this.emit();
  }

  /**
   * 记一次进度。**按题去重**：同一题重播三次也只报一次（否则慢网下会刷出一串请求，
   * 而且 `skipped` 会被后面的 `played` 覆盖成反的）。
   */
  private mark(position: number, status: 'played' | 'skipped'): void {
    const question = this.byPosition.get(position);
    if (question === undefined || question.id === '') return;
    if (this.marks.has(question.id)) return;
    this.marks.set(question.id, status);
    this.deps.markProgress(question.id, status);
  }

  private cancelPending(): void {
    this.cancelTimer?.();
    this.cancelTimer = null;
  }

  /**
   * 推进一格。
   *
   * 循环（而不是递归）：一格接一格地"开始"，直到需要等（或者播完）。
   * 停下来等的那一格把 `target` 记在 `timing` 里，`tick()` 会在到点时被再叫一次。
   */
  private tick(): void {
    if (!this.state.playing) return;
    if (this.plan.steps.length === 0) {
      this.finish();
      return;
    }

    let guard = 0;
    for (;;) {
      guard += 1;
      // 时间线上的格数是有限的；这个上限只是防"某个回归让 tick 自旋"
      if (guard > this.plan.steps.length + 8) {
        this.finish();
        return;
      }

      const timing = this.timing;
      if (timing === null) {
        const step = this.plan.steps[this.state.stepIndex];
        if (step === undefined) {
          this.finish();
          return;
        }
        // 开始这一格：读数格要么念、要么放录音；等待格什么都不做
        this.beginStep(step);
        continue;
      }

      const remaining = timing.target - this.deps.clock.now();
      if (remaining > 0) {
        this.cancelPending();
        this.cancelTimer = this.deps.clock.schedule(() => {
          this.tick();
        }, remaining);
        // 等待中也要让界面知道"还剩多久"（倒计时），所以这里要通知一次
        this.emit();
        return;
      }

      // 到点了 → 这一格算播完
      const step = this.plan.steps[this.state.stepIndex];
      const question = step ? this.byPosition.get(step.wordPosition) : undefined;
      if (step !== undefined && step.kind === 'word' && question !== undefined) {
        this.mark(step.wordPosition, 'played');
      }
      this.state = advancePlayback(this.state, this.plan.steps.length);
      this.timing = null;
      if (this.state.finished) {
        this.finish();
        return;
      }
    }
  }

  /**
   * 开始一格。
   *
   * `speaking` 的分支（等结束信号）只在"读数格真的用 TTS/录音"时出现；
   * 等待格直接按期结束（`fresh: false`：暂停后继续不重复做任何事）。
   */
  private beginStep(step: PlaybackStep): void {
    const current = this.timing;
    const now = this.deps.clock.now();

    if (step.kind !== 'word') {
      this.timing = {
        target: now + step.durationMs,
        fresh: current !== null ? false : true,
        speaking: false,
      };
      return;
    }

    const question = this.byPosition.get(step.wordPosition);
    const speaking = current?.speaking === true;
    if (speaking) {
      /**
       * 已经在念/已经在放了：只在第一次给它一个目标时刻。
       * ⚠️ 这里**不能**续期（`target = now + duration`）—— 每次被 tick 叫到都续期的话，
       * 这一格永远不会结束（循环自旋）。
       */
      if (current === null) {
        this.timing = { target: now + step.durationMs, fresh: true, speaking: true };
      }
      return;
    }

    if (question !== undefined && question.spokenSource === 'parent_audio' && question.audioPath !== null) {
      this.timing = { target: now + step.durationMs, fresh: true, speaking: true };
      this.deps.audio.play(question.audioPath, {
        onEnded: () => this.onSpokenEnd(),
        // 录音放不出来（格式不支持 / 文件坏了）**不能卡住整轮听写**：
        // 提前结束这一格，让它按原节奏继续（宁可少读一遍，也不要停在那里）。
        onError: () => this.endStepEarly(),
      });
      return;
    }

    // TTS 那条路：文本永远是词本身（`speechSynthesis` 读不了拼音）
    this.timing = { target: now + step.durationMs, fresh: true, speaking: true };
    this.deps.speech.speak(question?.spokenText || question?.wordText || '', {
      onEnd: () => this.onSpokenEnd(),
      onError: () => this.endStepEarly(),
    });
  }

  /** 念完/放完：这一格提前结束（真实时长可能比估算短）。 */
  private onSpokenEnd(): void {
    if (!this.state.playing || this.timing === null) return;
    this.timing = { ...this.timing, target: this.deps.clock.now() };
    this.tick();
  }

  /** 出声失败：这一格立刻结束，节奏继续往下走（绝不卡住整轮）。 */
  private endStepEarly(): void {
    if (!this.state.playing || this.timing === null) return;
    this.timing = { ...this.timing, target: this.deps.clock.now() };
    this.tick();
  }

  /** 释放：清掉待执行的定时器、停掉声音（组件卸载时调用）。 */
  dispose(): void {
    this.cancelPending();
    this.deps.speech.cancel();
    this.deps.audio.stop();
    this.listeners.clear();
  }
}

/** 秒 → `mm:ss`（播放器上的倒计时/进度用）。拿不到就是 `--:--`。 */
export function formatClock(totalSeconds: number | null | undefined): string {
  if (typeof totalSeconds !== 'number' || !Number.isFinite(totalSeconds) || totalSeconds < 0) {
    return '--:--';
  }
  const total = Math.floor(totalSeconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}
